import { createFileRoute } from "@tanstack/react-router";
import { requireTab } from "@/features/navigation/requireTab";
import Title from "../../../components/common/Title";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@patient-watch/ui/card";
import { useHelpCopy } from "@/features/i18n/helpCopy";
import Call from "@/components/common/Call";
import { HelpIssueList } from "@/features/help/components/HelpIssueList";
import { HelpPatientBenefitGrid } from "@/features/help/components/HelpPatientBenefitGrid";
import { HelpPatientAppFlowPreview } from "@/features/help/previews/HelpPatientAppFlowPreview";

const PatientHelpPage = () => {
    const copy = useHelpCopy();

    return (
        <section className="lg-width">
            <Title words={copy.patientTitle} />

            <p className="px-4 pb-4 text-muted-foreground">{copy.patientIntro}</p>

            <div className="space-y-8 px-4 py-4">
                <Card>
                    <CardHeader>
                        <CardTitle>{copy.patientWhyTitle}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-muted-foreground">{copy.patientWhyDescription}</p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{copy.patientBenefitsTitle}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <HelpPatientBenefitGrid />
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{copy.patientHowItWorksTitle}</CardTitle>
                        <CardDescription>{copy.patientHowItWorksDescription}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-8">
                        <div className="space-y-4">
                            {copy.patientHowItWorksSteps.map((step, index) => (
                                <div key={step.title} className="flex items-start gap-4">
                                    <div className="flex flex-shrink-0 justify-center items-center bg-indigo-100 dark:bg-indigo-900 rounded-full w-8 h-8 font-medium text-indigo-700 dark:text-indigo-300">
                                        {index + 1}
                                    </div>
                                    <div className="flex-1 space-y-1">
                                        <h4 className="font-medium">{step.title}</h4>
                                        <p className="text-muted-foreground text-sm">{step.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <HelpPatientAppFlowPreview />

                        <Call variant="info" size="sm" words={copy.patientScoringTipTitle}>
                            <p className="mt-1 text-sm">{copy.patientScoringTipDescription}</p>
                        </Call>

                        <Call variant="blueSoft" size="sm" words={copy.important}>
                            <ul className="space-y-1 mt-1 list-disc list-inside">
                                {copy.importantItems.map((item) => (
                                    <li key={item}>{item}</li>
                                ))}
                            </ul>
                        </Call>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{copy.gettingStarted}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <h3 className="font-semibold">{copy.quickStart}</h3>
                        <ol className="space-y-2 list-decimal list-inside">
                            {copy.quickStartSteps.map((step) => (
                                <li key={step}>{step}</li>
                            ))}
                        </ol>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{copy.needHelp}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <ul className="space-y-2 list-disc list-inside">
                            {copy.needHelpItems.map((item) => (
                                <li key={item}>{item}</li>
                            ))}
                        </ul>
                        <HelpIssueList issues={copy.patientCommonIssues} />
                    </CardContent>
                </Card>
            </div>
        </section>
    );
};

export const Route = createFileRoute("/_app/help/patient")({
    beforeLoad: ({ context }) => requireTab(context, "help"),
    component: PatientHelpPage,
});
